import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { format, subDays } from 'date-fns';
import RevenueMixReport from '@/pages/RevenueMixReport';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, FunnelChart, Funnel, LabelList
} from 'recharts';
import {
  BarChart3, Trophy, CalendarDays, TrendingUp, Zap, Wallet, Briefcase,
  Target, AlarmClock, Search, LineChart as LineChartIcon, Clock, Mail,
} from 'lucide-react';

const COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#EF4444', '#6366F1'];

// Custom dropdown for the Report Type selector — native <select> can't
// render React icon components inside <option>, so we build a minimal
// button+popover with real lucide icons grouped by section.
function ReportTypeDropdown({ value, onChange, tabs, tabsByGroup, groupOrder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const selected = tabs.find((t) => t.id === value);
  const SelectedIcon = selected?.Icon;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 font-medium hover:border-slate-400 transition-colors"
        data-testid="report-type-selector"
      >
        <span className="flex items-center gap-2 truncate">
          {SelectedIcon && <SelectedIcon className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />}
          <span className="truncate">{selected?.name || 'Select report'}</span>
        </span>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-96 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1">
          {groupOrder.map((group) =>
            (tabsByGroup[group] || []).length > 0 ? (
              <div key={group}>
                <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {group}
                </div>
                {tabsByGroup[group].map((t) => {
                  const Icon = t.Icon;
                  const active = t.id === value;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => { onChange(t.id); setOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                        active
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60'
                      }`}
                      data-testid={`report-type-option-${t.id}`}
                    >
                      {Icon && <Icon className="w-4 h-4 flex-shrink-0 opacity-70" />}
                      <span className="truncate">{t.name}</span>
                    </button>
                  );
                })}
              </div>
            ) : null,
          )}
        </div>
      )}
    </div>
  );
}

function ReportsPage() {
  const [activeTab, setActiveTab] = useState('executive');
  const [period, setPeriod] = useState('this_month');
  const [customDates, setCustomDates] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(true);
  
  // Report data states
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [conversionFunnel, setConversionFunnel] = useState(null);
  const [pipelineVelocity, setPipelineVelocity] = useState(null);
  const [revenueForecast, setRevenueForecast] = useState(null);
  const [winLossAnalysis, setWinLossAnalysis] = useState(null);
  const [dealAging, setDealAging] = useState(null);
  const [leadSourceROI, setLeadSourceROI] = useState(null);
  const [activityEffectiveness, setActivityEffectiveness] = useState(null);
  const [bestTimeToContact, setBestTimeToContact] = useState(null);
  const [teamComparison, setTeamComparison] = useState(null);
  const [scheduledReports, setScheduledReports] = useState([]);
  
  // Modal states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    report_type: 'executive_summary',
    frequency: 'weekly',
    recipients: '',
    period: 'this_week',
    send_time: '09:00',
    send_day: 0,
    timezone: 'Asia/Kolkata'
  });

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value?.toLocaleString('en-IN') || 0}`;
  };

  const getDateParams = useCallback(() => {
    if (period === 'custom' && customDates.start && customDates.end) {
      return `period=custom&start_date=${customDates.start}&end_date=${customDates.end}`;
    }
    return `period=${period}`;
  }, [period, customDates]);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const params = getDateParams();
      
      // Fetch data based on active tab
      if (activeTab === 'executive') {
        const res = await axios.get(`${API}/reports/executive-summary?${params}`);
        setExecutiveSummary(res.data);
      } else if (activeTab === 'leaderboard') {
        const res = await axios.get(`${API}/reports/leaderboard?${params}`);
        setLeaderboard(res.data);
      } else if (activeTab === 'funnel') {
        const res = await axios.get(`${API}/reports/conversion-funnel?${params}`);
        setConversionFunnel(res.data);
      } else if (activeTab === 'velocity') {
        const res = await axios.get(`${API}/reports/pipeline-velocity?${params}`);
        setPipelineVelocity(res.data);
      } else if (activeTab === 'forecast') {
        const res = await axios.get(`${API}/reports/revenue-forecast`);
        setRevenueForecast(res.data);
      } else if (activeTab === 'winloss') {
        const res = await axios.get(`${API}/reports/win-loss-analysis?${params}`);
        setWinLossAnalysis(res.data);
      } else if (activeTab === 'aging') {
        const res = await axios.get(`${API}/reports/deal-aging`);
        setDealAging(res.data);
      } else if (activeTab === 'source') {
        const res = await axios.get(`${API}/reports/lead-source-roi?${params}`);
        setLeadSourceROI(res.data);
      } else if (activeTab === 'effectiveness') {
        const res = await axios.get(`${API}/reports/activity-effectiveness?${params}`);
        setActivityEffectiveness(res.data);
      } else if (activeTab === 'timing') {
        const res = await axios.get(`${API}/reports/best-time-to-contact?${params}`);
        setBestTimeToContact(res.data);
      } else if (activeTab === 'comparison') {
        const res = await axios.get(`${API}/reports/team-comparison?period=${period}`);
        setTeamComparison(res.data);
      } else if (activeTab === 'scheduled') {
        const res = await axios.get(`${API}/reports/scheduled`);
        setScheduledReports(res.data);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, getDateParams, period]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleScheduleReport = async () => {
    try {
      await axios.post(`${API}/reports/scheduled`, {
        ...newSchedule,
        recipients: newSchedule.recipients.split(',').map(e => e.trim())
      });
      setShowScheduleModal(false);
      setNewSchedule({ name: '', report_type: 'executive_summary', frequency: 'weekly', recipients: '', period: 'this_week' });
      if (activeTab === 'scheduled') fetchReportData();
    } catch (error) {
      console.error('Error scheduling report:', error);
    }
  };

  const deleteScheduledReport = async (id) => {
    try {
      await axios.delete(`${API}/reports/scheduled/${id}`);
      fetchReportData();
    } catch (error) {
      console.error('Error deleting report:', error);
    }
  };

  const tabs = [
    { id: 'executive', name: 'Executive Summary', Icon: BarChart3, group: 'Performance Reports' },
    { id: 'leaderboard', name: 'SDR Leaderboard', Icon: Trophy, group: 'Performance Reports' },
    { id: 'comparison', name: 'Period Comparison', Icon: CalendarDays, group: 'Performance Reports' },
    { id: 'funnel', name: 'Conversion Funnel', Icon: TrendingUp, group: 'Pipeline & Revenue' },
    { id: 'velocity', name: 'Pipeline Velocity', Icon: Zap, group: 'Pipeline & Revenue' },
    { id: 'forecast', name: 'Revenue Forecast', Icon: Wallet, group: 'Pipeline & Revenue' },
    { id: 'revenue_mix', name: 'Revenue Mix', Icon: Briefcase, group: 'Pipeline & Revenue' },
    { id: 'winloss', name: 'Win/Loss Analysis', Icon: Target, group: 'Pipeline & Revenue' },
    { id: 'aging', name: 'Deal Aging', Icon: AlarmClock, group: 'Pipeline & Revenue' },
    { id: 'source', name: 'Lead Source ROI', Icon: Search, group: 'Activity Intelligence' },
    { id: 'effectiveness', name: 'Activity Effectiveness', Icon: LineChartIcon, group: 'Activity Intelligence' },
    { id: 'timing', name: 'Best Time to Contact', Icon: Clock, group: 'Activity Intelligence' },
    { id: 'scheduled', name: 'Scheduled Reports', Icon: Mail, group: 'Settings' },
  ];

  // Group tabs for the dropdown so adding a new tab above auto-propagates
  const tabsByGroup = tabs.reduce((acc, t) => {
    (acc[t.group] ||= []).push(t);
    return acc;
  }, {});
  const groupOrder = ['Performance Reports', 'Pipeline & Revenue', 'Activity Intelligence', 'Settings'];

  const renderExecutiveSummary = () => {
    if (!executiveSummary) return null;
    const { pipeline, revenue, activities, leads, conversion } = executiveSummary;
    
    return (
      <div className="space-y-6">
        {/* Top KPIs - Clean style with BLACK values */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Total Pipeline</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(pipeline?.total_value)}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{pipeline?.deal_count} deals</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Revenue Won</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(revenue?.closed_won)}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{revenue?.deal_count} deals closed</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Win Rate</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{conversion?.win_rate}%</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{conversion?.won} won / {conversion?.lost} lost</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Avg Deal Size</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(revenue?.avg_deal_size)}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Per closed deal</p>
          </div>
        </div>

        {/* Activity & Leads */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Activity Summary</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-xl font-bold text-slate-900 dark:text-white">{activities?.calls}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Calls</p>
              </div>
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-xl font-bold text-slate-900 dark:text-white">{activities?.emails}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Emails</p>
              </div>
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-xl font-bold text-slate-900 dark:text-white">{activities?.meetings}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Meetings</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Lead Metrics</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-xl font-bold text-slate-900 dark:text-white">{leads?.new}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">New Leads</p>
              </div>
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-xl font-bold text-slate-900 dark:text-white">{leads?.qualified}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Qualified</p>
              </div>
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-xl font-bold text-slate-900 dark:text-white">{leads?.qualification_rate}%</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">Qual Rate</p>
              </div>
            </div>
          </div>
        </div>

        {/* Weighted Pipeline */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Pipeline Health</h3>
          <div className="flex items-center gap-8">
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Weighted Pipeline (by probability)</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(pipeline?.weighted_value)}</p>
            </div>
            <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-slate-700 rounded-full transition-all"
                style={{ width: `${Math.min((pipeline?.weighted_value / (pipeline?.total_value || 1)) * 100, 100)}%` }}
              />
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Pipeline</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{formatCurrency(pipeline?.total_value)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLeaderboard = () => {
    if (!leaderboard.length) return <p className="text-center text-slate-500 dark:text-slate-400 py-8">No data available</p>;
    
    return (
      <div className="space-y-6">
        {/* Top 3 - Clean style */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {leaderboard.slice(0, 3).map((user, idx) => (
            <div key={user.user_id} className={`bg-white dark:bg-slate-800 rounded-lg border p-6 ${idx === 0 ? 'border-amber-300 ring-1 ring-amber-200' : idx === 1 ? 'border-slate-300 dark:border-slate-600' : 'border-slate-300 dark:border-slate-600'}`}>
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-slate-200 text-slate-700 dark:text-slate-300' : 'bg-orange-100 text-orange-700'}`}>
                  #{idx + 1}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-slate-900 dark:text-white">{user.name}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 capitalize">{user.role}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                <div className="p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <p className="text-lg font-bold text-slate-900 dark:text-white">{formatCurrency(user.won_revenue)}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Revenue</p>
                </div>
                <div className="p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <p className="text-lg font-bold text-slate-900 dark:text-white">{user.deals_won}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Deals Won</p>
                </div>
              </div>
              <div className="mt-2 text-center">
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{user.score}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Score</p>
              </div>
            </div>
          ))}
        </div>

        {/* Full Table */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Full Rankings</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Calls</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Emails</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Meetings</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Demos</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Qualified</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Deals Won</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Revenue</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leaderboard.map((user) => (
                  <tr key={user.user_id} className="hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900">
                    <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">#{user.rank}</td>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{user.name}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{user.total_calls}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{user.total_emails}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{user.total_meetings}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{user.total_demos}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{user.qualified_leads}</td>
                    <td className="px-4 py-3 text-slate-900 dark:text-white font-semibold">{user.deals_won}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900 dark:text-white">{formatCurrency(user.won_revenue)}</td>
                    <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">{user.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderConversionFunnel = () => {
    if (!conversionFunnel) return null;
    
    // Handle missing data gracefully
    const funnel = Array.isArray(conversionFunnel.funnel) ? conversionFunnel.funnel : [];
    const conversion_rates = conversionFunnel.conversion_rates || {};
    
    return (
      <div className="space-y-6">
        {/* Conversion Rate KPIs - Clean style with BLACK values */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Lead → Contact</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{conversion_rates.lead_to_contact || 0}%</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Contact → Meeting</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{conversion_rates.contact_to_meeting || 0}%</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Proposal → Won</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{conversion_rates.proposal_to_won || 0}%</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Overall Win Rate</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{conversion_rates.overall_win_rate || 0}%</p>
          </div>
        </div>

        {/* Funnel Visualization */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Sales Funnel</h3>
          {funnel.length > 0 ? (
            <div className="space-y-2">
              {funnel.map((stage, idx) => (
                <div key={stage.stage || idx} className="flex items-center gap-4">
                  <div className="w-32 text-right text-sm font-medium text-slate-700 dark:text-slate-300">{stage.stage || '-'}</div>
                  <div className="flex-1 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden relative">
                    <div 
                      className="h-full rounded-lg transition-all"
                      style={{ 
                        width: `${stage.percentage || 0}%`,
                        backgroundColor: COLORS[idx % COLORS.length]
                      }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-slate-900 dark:text-white">
                      {stage.count || 0} ({stage.percentage || 0}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400 dark:text-slate-500">No funnel data available</div>
          )}
        </div>

        {/* Funnel Chart */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Funnel Chart</h3>
          {funnel.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={funnel} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" stroke="#64748B" />
                <YAxis type="category" dataKey="stage" stroke="#64748B" width={100} />
                <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                <Bar dataKey="count" fill="#334155" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
              No funnel data available
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPipelineVelocity = () => {
    if (!pipelineVelocity) return null;
    
    // Ensure velocity_by_stage is an array
    const velocityByStage = Array.isArray(pipelineVelocity.velocity_by_stage) 
      ? pipelineVelocity.velocity_by_stage 
      : [];
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Avg Sales Cycle</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{pipelineVelocity.avg_sales_cycle_days || 0} days</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Deals Closed</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{pipelineVelocity.total_closed_deals || 0}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Won Deals</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{pipelineVelocity.won_deals || 0}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Time in Each Stage (Avg Days)</h3>
          {velocityByStage.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={velocityByStage}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="stage" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                <Bar dataKey="avg_days" fill="#334155" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
              No pipeline velocity data available
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRevenueForecast = () => {
    if (!revenueForecast) return null;
    
    // Convert by_stage object to array for mapping
    const byStageData = revenueForecast.by_stage 
      ? Object.entries(revenueForecast.by_stage).map(([stage, data]) => ({
          stage,
          ...data
        }))
      : [];
    
    // Ensure by_month is an array
    const byMonthData = Array.isArray(revenueForecast.by_month) ? revenueForecast.by_month : [];
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Total Pipeline</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(revenueForecast.total_pipeline || 0)}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Weighted Pipeline</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(revenueForecast.weighted_pipeline || 0)}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Last 90 Days Revenue</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(revenueForecast.historical_revenue_90_days || 0)}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Active Deals</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{revenueForecast.deal_count || 0}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Pipeline by Stage</h3>
            {byStageData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={byStageData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="weighted"
                    label={({ stage, weighted }) => `${stage}: ${formatCurrency(weighted || 0)}`}
                  >
                    {byStageData.map((entry, index) => (
                      <Cell key={`cell-${entry.stage || index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
                No pipeline data available
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Monthly Forecast</h3>
            {byMonthData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={byMonthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#64748B" />
                  <YAxis stroke="#64748B" tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Area type="monotone" dataKey="weighted" stroke="#334155" fill="#94a3b8" name="Weighted Value" />
                  <Area type="monotone" dataKey="value" stroke="#64748B" fill="#cbd5e1" name="Total Value" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
                No monthly forecast data available
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderWinLossAnalysis = () => {
    if (!winLossAnalysis) return null;
    
    // Handle different API response structures
    const summary = winLossAnalysis.summary || {
      won_count: winLossAnalysis.won?.count || 0,
      won_revenue: winLossAnalysis.won?.value || 0,
      lost_count: winLossAnalysis.lost?.count || 0,
      lost_revenue: winLossAnalysis.lost?.value || 0,
      win_rate: winLossAnalysis.win_rate || 0,
      avg_won_deal: winLossAnalysis.won?.count > 0 
        ? (winLossAnalysis.won?.value || 0) / winLossAnalysis.won?.count 
        : 0
    };
    
    // Convert by_industry object to array
    const byIndustryWon = winLossAnalysis.by_industry?.won 
      ? (Array.isArray(winLossAnalysis.by_industry.won) 
          ? winLossAnalysis.by_industry.won 
          : Object.entries(winLossAnalysis.by_industry.won).map(([industry, data]) => ({
              industry,
              count: data.count || 0,
              value: data.value || 0
            })))
      : [];
    
    // Convert by_source object to array  
    const bySourceWon = winLossAnalysis.by_source?.won 
      ? (Array.isArray(winLossAnalysis.by_source.won)
          ? winLossAnalysis.by_source.won
          : Object.entries(winLossAnalysis.by_source.won).map(([source, data]) => ({
              source,
              count: data.count || 0,
              value: data.value || 0
            })))
      : [];
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Won</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.won_count || 0}</p>
            <p className="text-xs text-green-600 mt-1">{formatCurrency(summary.won_revenue || 0)}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Lost</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.lost_count || 0}</p>
            <p className="text-xs text-red-600 mt-1">{formatCurrency(summary.lost_revenue || 0)}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Win Rate</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.win_rate || 0}%</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Avg Won Deal</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(summary.avg_won_deal || 0)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">By Industry</h3>
            {byIndustryWon.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byIndustryWon}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="industry" stroke="#64748B" angle={-45} textAnchor="end" height={80} />
                  <YAxis stroke="#64748B" />
                  <Tooltip />
                  <Bar dataKey="count" fill="#334155" name="Won" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
                No industry data available
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">By Lead Source</h3>
            {bySourceWon.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={bySourceWon}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="source" stroke="#64748B" />
                  <YAxis stroke="#64748B" />
                  <Tooltip formatter={(value, name) => [name === 'value' ? formatCurrency(value) : value, name === 'value' ? 'Revenue' : 'Count']} />
                  <Bar dataKey="count" fill="#64748B" name="Deals Won" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
                No source data available
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDealAging = () => {
    if (!dealAging) return null;
    
    // Handle different API response structures - 'buckets' or 'aging_buckets'
    const bucketsData = dealAging.aging_buckets || dealAging.buckets || {};
    const agingData = Object.entries(bucketsData).map(([bucket, data]) => ({
      bucket,
      count: data?.count || 0,
      value: data?.total_value || data?.value || 0
    }));
    
    // Ensure stale_deals is an array
    const staleDeals = Array.isArray(dealAging.stale_deals) ? dealAging.stale_deals : [];
    const totalOpenDeals = dealAging.total_open_deals || dealAging.total_deals || 0;
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Total Open Deals</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{totalOpenDeals}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Stale Deals (14+ days)</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{dealAging.stale_deal_count || 0}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">At Risk Value</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(dealAging.stale_deal_value || 0)}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Deal Age Distribution</h3>
          {agingData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={agingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="bucket" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip formatter={(value, name) => [name === 'value' ? formatCurrency(value) : value, name === 'value' ? 'Value' : 'Count']} />
                <Bar dataKey="count" fill="#334155" name="Deals" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
              No deal aging data available
            </div>
          )}
        </div>

        {/* Stale Deals List */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Stale Deals Requiring Attention</h3>
            <span className="px-3 py-1 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-sm font-medium">{dealAging.stale_deal_count || 0} deals</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Lead Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Deal Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Days Since Activity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Last Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {staleDeals.length > 0 ? (
                  staleDeals.slice(0, 10).map((deal, idx) => (
                    <tr key={deal.id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                      <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{deal.name || '-'}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{deal.company || '-'}</td>
                      <td className="px-4 py-3"><span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-700 dark:text-slate-300">{deal.status || '-'}</span></td>
                      <td className="px-4 py-3 font-semibold text-slate-900 dark:text-white">{formatCurrency(deal.deal_value || 0)}</td>
                      <td className={`px-4 py-3 font-semibold ${(deal.days_since_activity || 0) > 30 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>{deal.days_since_activity || 0} days</td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-sm">{deal.last_activity_date ? format(new Date(deal.last_activity_date), 'MMM dd, yyyy') : '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-400 dark:text-slate-500">
                      No stale deals found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderLeadSourceROI = () => {
    if (!leadSourceROI) return null;
    
    // Ensure sources is an array
    const sources = Array.isArray(leadSourceROI.sources) ? leadSourceROI.sources : [];
    
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Lead Source Performance</h3>
          {sources.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sources}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="source" stroke="#64748B" />
                <YAxis yAxisId="left" stroke="#64748B" />
                <YAxis yAxisId="right" orientation="right" stroke="#334155" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="total_leads" fill="#64748B" name="Total Leads" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="left" dataKey="won_count" fill="#334155" name="Won" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
              No lead source data available
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Source Details</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Total Leads</th>
                  <th>Qualified</th>
                  <th>Qual Rate</th>
                  <th>Won</th>
                  <th>Win Rate</th>
                  <th>Revenue</th>
                  <th>Avg Deal</th>
                </tr>
              </thead>
              <tbody>
                {sources.length > 0 ? (
                  sources.map((source, idx) => (
                    <tr key={source.source || idx}>
                      <td className="font-medium">{source.source || '-'}</td>
                      <td>{source.total_leads || 0}</td>
                      <td>{source.qualified_count || 0}</td>
                      <td className="text-purple-600">{source.qualification_rate || 0}%</td>
                      <td className="text-emerald-600 font-semibold">{source.won_count || 0}</td>
                      <td className="text-blue-600">{source.win_rate || 0}%</td>
                      <td className="font-semibold">{formatCurrency(source.won_revenue || 0)}</td>
                      <td>{formatCurrency(source.avg_deal_value || 0)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-400 dark:text-slate-500">
                      No source data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderActivityEffectiveness = () => {
    if (!activityEffectiveness) return null;
    
    // Handle missing data gracefully
    const won_deals = activityEffectiveness.won_deals || { count: 0, avg_per_deal: { calls: 0, emails: 0, meetings: 0, demos: 0 } };
    const lost_deals = activityEffectiveness.lost_deals || { count: 0, avg_per_deal: { calls: 0, emails: 0, meetings: 0, demos: 0 } };
    const insights = activityEffectiveness.insights || { calls_difference: 0, meetings_difference: 0, demos_difference: 0 };
    
    const comparisonData = [
      { metric: 'Calls', won: won_deals.avg_per_deal?.calls || 0, lost: lost_deals.avg_per_deal?.calls || 0 },
      { metric: 'Emails', won: won_deals.avg_per_deal?.emails || 0, lost: lost_deals.avg_per_deal?.emails || 0 },
      { metric: 'Meetings', won: won_deals.avg_per_deal?.meetings || 0, lost: lost_deals.avg_per_deal?.meetings || 0 },
      { metric: 'Demos', won: won_deals.avg_per_deal?.demos || 0, lost: lost_deals.avg_per_deal?.demos || 0 },
    ];
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Won Deals Analyzed</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{won_deals.count || 0}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-sm font-medium text-rose-600 dark:text-rose-400">Lost Deals Analyzed</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{lost_deals.count || 0}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">More Calls = Win</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{(insights.calls_difference || 0) > 0 ? '+' : ''}{insights.calls_difference || 0}</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-center border border-slate-100 dark:border-slate-700">
            <p className="text-sm font-medium text-purple-600 dark:text-purple-400">More Meetings = Win</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{(insights.meetings_difference || 0) > 0 ? '+' : ''}{insights.meetings_difference || 0}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Won vs Lost - Avg Activities per Deal</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={comparisonData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="metric" stroke="#64748B" />
              <YAxis stroke="#64748B" />
              <Tooltip />
              <Legend />
              <Bar dataKey="won" fill="#10B981" name="Won Deals" radius={[4, 4, 0, 0]} />
              <Bar dataKey="lost" fill="#EF4444" name="Lost Deals" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Key Insight</h3>
          <p className="text-slate-700 dark:text-slate-300">
            Won deals have on average <span className="font-bold text-emerald-600">{insights.calls_difference || 0}</span> more calls, 
            <span className="font-bold text-purple-600"> {insights.meetings_difference || 0}</span> more meetings, and 
            <span className="font-bold text-blue-600"> {insights.demos_difference || 0}</span> more demos than lost deals.
          </p>
        </div>
      </div>
    );
  };

  const renderBestTimeToContact = () => {
    if (!bestTimeToContact) return null;
    
    // Ensure arrays exist
    const bestHours = Array.isArray(bestTimeToContact.best_hours) ? bestTimeToContact.best_hours : [];
    const bestDays = Array.isArray(bestTimeToContact.best_days) ? bestTimeToContact.best_days : [];
    const hourlyBreakdown = Array.isArray(bestTimeToContact.hourly_breakdown) 
      ? bestTimeToContact.hourly_breakdown.filter(h => h.total_activities > 0) 
      : [];
    
    return (
      <div className="space-y-6">
        {/* Best Times */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Best Hours to Contact</h3>
            <div className="space-y-3">
              {bestHours.length > 0 ? (
                bestHours.map((hour, idx) => (
                  <div key={hour.hour || idx} className="flex items-center gap-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                    <span className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold">#{idx + 1}</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900 dark:text-white">{hour.hour || '-'}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{hour.total_activities || 0} activities, {hour.successful || 0} successful</p>
                    </div>
                    <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{hour.success_rate || 0}%</span>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-slate-400 dark:text-slate-500">No hourly data available</div>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Best Days to Contact</h3>
            <div className="space-y-3">
              {bestDays.length > 0 ? (
                bestDays.map((day, idx) => (
                  <div key={day.day || idx} className="flex items-center gap-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">#{idx + 1}</span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900 dark:text-white">{day.day || '-'}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{day.total_activities || 0} activities, {day.successful || 0} successful</p>
                    </div>
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{day.success_rate || 0}%</span>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-slate-400 dark:text-slate-500">No daily data available</div>
              )}
            </div>
          </div>
        </div>

        {/* Hourly Heatmap */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Hourly Activity Heatmap</h3>
          {hourlyBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlyBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="hour" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip />
                <Bar dataKey="success_rate" fill="#10B981" name="Success Rate %" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
              No hourly breakdown data available
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTeamComparison = () => {
    if (!teamComparison) return null;
    
    // Handle missing data gracefully
    const current_period = teamComparison.current_period || { label: 'Current', stats: {} };
    const compare_period = teamComparison.compare_period || { label: 'Previous', stats: {} };
    const changes = teamComparison.changes || {};
    
    const currentStats = current_period.stats || {};
    const compareStats = compare_period.stats || {};
    
    const comparisonData = [
      { metric: 'Leads', current: currentStats.leads || 0, previous: compareStats.leads || 0, change: changes.leads || 0 },
      { metric: 'Won', current: currentStats.won || 0, previous: compareStats.won || 0, change: changes.won || 0 },
      { metric: 'Calls', current: currentStats.calls || 0, previous: compareStats.calls || 0, change: changes.calls || 0 },
      { metric: 'Emails', current: currentStats.emails || 0, previous: compareStats.emails || 0, change: changes.emails || 0 },
      { metric: 'Meetings', current: currentStats.meetings || 0, previous: compareStats.meetings || 0, change: changes.meetings || 0 },
    ];
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`bg-white dark:bg-slate-800 rounded-lg border p-4 ${(changes.revenue || 0) >= 0 ? 'border-emerald-200 dark:border-emerald-800' : 'border-rose-200 dark:border-rose-800'}`}>
            <p className="text-sm text-slate-500 dark:text-slate-400">Revenue Change</p>
            <p className={`text-3xl font-bold ${(changes.revenue || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              {(changes.revenue || 0) >= 0 ? '+' : ''}{changes.revenue || 0}%
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400">{formatCurrency(currentStats.revenue || 0)} vs {formatCurrency(compareStats.revenue || 0)}</p>
          </div>
          <div className={`bg-white dark:bg-slate-800 rounded-lg border p-4 ${(changes.won || 0) >= 0 ? 'border-emerald-200 dark:border-emerald-800' : 'border-rose-200 dark:border-rose-800'}`}>
            <p className="text-sm text-slate-500 dark:text-slate-400">Deals Won Change</p>
            <p className={`text-3xl font-bold ${(changes.won || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              {(changes.won || 0) >= 0 ? '+' : ''}{changes.won || 0}%
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400">{currentStats.won || 0} vs {compareStats.won || 0}</p>
          </div>
          <div className={`bg-white dark:bg-slate-800 rounded-lg border p-4 ${(changes.win_rate || 0) >= 0 ? 'border-emerald-200 dark:border-emerald-800' : 'border-rose-200 dark:border-rose-800'}`}>
            <p className="text-sm text-slate-500 dark:text-slate-400">Win Rate Change</p>
            <p className={`text-3xl font-bold ${(changes.win_rate || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              {(changes.win_rate || 0) >= 0 ? '+' : ''}{changes.win_rate || 0}%
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400">{currentStats.win_rate || 0}% vs {compareStats.win_rate || 0}%</p>
          </div>
          <div className={`bg-white dark:bg-slate-800 rounded-lg border p-4 ${(changes.meetings || 0) >= 0 ? 'border-emerald-200 dark:border-emerald-800' : 'border-rose-200 dark:border-rose-800'}`}>
            <p className="text-sm text-slate-500 dark:text-slate-400">Meetings Change</p>
            <p className={`text-3xl font-bold ${(changes.meetings || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              {(changes.meetings || 0) >= 0 ? '+' : ''}{changes.meetings || 0}%
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400">{currentStats.meetings || 0} vs {compareStats.meetings || 0}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">{current_period.label || 'Current'} vs {compare_period.label || 'Previous'}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={comparisonData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="metric" stroke="#64748B" />
              <YAxis stroke="#64748B" />
              <Tooltip />
              <Legend />
              <Bar dataKey="current" fill="#3B82F6" name={current_period.label || 'Current'} radius={[4, 4, 0, 0]} />
              <Bar dataKey="previous" fill="#94A3B8" name={compare_period.label || 'Previous'} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderScheduledReports = () => {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Scheduled Reports</h2>
            <p className="text-slate-500 dark:text-slate-400">Auto-email reports to your team</p>
          </div>
          <button
            onClick={() => setShowScheduleModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Schedule New Report
          </button>
        </div>

        {scheduledReports.length === 0 ? (
          <div className="card p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-slate-500 dark:text-slate-400">No scheduled reports yet</p>
            <p className="text-sm text-slate-400 mt-1">Create one to auto-email reports to your team</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {scheduledReports.map((report) => (
              <div key={report.id} className="card p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${report.enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">{report.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{report.report_type} • {report.frequency} • {report.recipients.length} recipients</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${report.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500 dark:text-slate-400'}`}>
                    {report.enabled ? 'Active' : 'Paused'}
                  </span>
                  <button
                    onClick={() => deleteScheduledReport(report.id)}
                    className="p-2 text-slate-400 hover:text-rose-600 transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'executive': return renderExecutiveSummary();
      case 'revenue_mix': return <RevenueMixReport />;
      case 'leaderboard': return renderLeaderboard();
      case 'funnel': return renderConversionFunnel();
      case 'velocity': return renderPipelineVelocity();
      case 'forecast': return renderRevenueForecast();
      case 'winloss': return renderWinLossAnalysis();
      case 'aging': return renderDealAging();
      case 'source': return renderLeadSourceROI();
      case 'effectiveness': return renderActivityEffectiveness();
      case 'timing': return renderBestTimeToContact();
      case 'comparison': return renderTeamComparison();
      case 'scheduled': return renderScheduledReports();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6 p-4 lg:p-6 space-y-5" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Analytics & Reports</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Comprehensive insights into your sales performance</p>
        </div>
      </div>

      {/* Controls Row */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Report Type Dropdown — custom popover so we can render real
              lucide icons inside each option (native <select> can't). */}
          <div className="flex-1 sm:max-w-xs">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1 block">Report Type</label>
            <ReportTypeDropdown
              value={activeTab}
              onChange={setActiveTab}
              tabs={tabs}
              tabsByGroup={tabsByGroup}
              groupOrder={groupOrder}
            />
          </div>

          {/* Date Range Dropdown */}
          <div className="sm:w-40">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1 block">Period</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800"
              data-testid="period-selector"
            >
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_quarter">This Quarter</option>
              <option value="this_year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          
          {/* Custom Date Inputs */}
          {period === 'custom' && (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1 block">Start Date</label>
                <input
                  type="date"
                  value={customDates.start}
                  onChange={(e) => setCustomDates(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1 block">End Date</label>
                <input
                  type="date"
                  value={customDates.end}
                  onChange={(e) => setCustomDates(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400"
                />
              </div>
            </>
          )}

          {/* Schedule Button */}
          <div className="sm:self-end">
            <button
              onClick={() => setShowScheduleModal(true)}
              className="w-full sm:w-auto px-4 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition flex items-center justify-center gap-2"
              data-testid="schedule-report-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Schedule</span>
            </button>
          </div>
        </div>
      </div>

      {/* Current Report Title */}
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">{tabs.find(t => t.id === activeTab)?.name}</h2>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-slate-200 dark:border-slate-700 border-t-slate-600 rounded-full animate-spin"></div>
        </div>
      ) : (
        renderTabContent()
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Schedule Report</h3>
            <div className="space-y-4">
              <div>
                <label className="form-label">Report Name *</label>
                <input
                  type="text"
                  value={newSchedule.name}
                  onChange={(e) => setNewSchedule(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500"
                  placeholder="Weekly Sales Summary"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Report Type</label>
                  <select
                    value={newSchedule.report_type}
                    onChange={(e) => setNewSchedule(prev => ({ ...prev, report_type: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    <option value="executive_summary">Executive Summary</option>
                    <option value="leaderboard">Leaderboard</option>
                    <option value="pipeline">Pipeline Report</option>
                    <option value="win_loss">Win/Loss Analysis</option>
                    <option value="conversion_funnel">Conversion Funnel</option>
                    <option value="revenue_forecast">Revenue Forecast</option>
                    <option value="deal_aging">Deal Aging</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Frequency</label>
                  <select
                    value={newSchedule.frequency}
                    onChange={(e) => setNewSchedule(prev => ({ ...prev, frequency: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>

              {/* Time Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Send Time (IST)</label>
                  <input
                    type="time"
                    value={newSchedule.send_time}
                    onChange={(e) => setNewSchedule(prev => ({ ...prev, send_time: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                
                {newSchedule.frequency === 'weekly' && (
                  <div>
                    <label className="form-label">Day of Week</label>
                    <select
                      value={newSchedule.send_day}
                      onChange={(e) => setNewSchedule(prev => ({ ...prev, send_day: parseInt(e.target.value) }))}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      <option value={0}>Monday</option>
                      <option value={1}>Tuesday</option>
                      <option value={2}>Wednesday</option>
                      <option value={3}>Thursday</option>
                      <option value={4}>Friday</option>
                      <option value={5}>Saturday</option>
                      <option value={6}>Sunday</option>
                    </select>
                  </div>
                )}
                
                {newSchedule.frequency === 'monthly' && (
                  <div>
                    <label className="form-label">Day of Month</label>
                    <select
                      value={newSchedule.send_day}
                      onChange={(e) => setNewSchedule(prev => ({ ...prev, send_day: parseInt(e.target.value) }))}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      {[...Array(28)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="form-label">Data Period</label>
                <select
                  value={newSchedule.period}
                  onChange={(e) => setNewSchedule(prev => ({ ...prev, period: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500"
                >
                  <option value="today">Today's Data</option>
                  <option value="this_week">This Week's Data</option>
                  <option value="last_week">Last Week's Data</option>
                  <option value="this_month">This Month's Data</option>
                  <option value="last_month">Last Month's Data</option>
                </select>
              </div>

              <div>
                <label className="form-label">Recipients (comma-separated emails) *</label>
                <input
                  type="text"
                  value={newSchedule.recipients}
                  onChange={(e) => setNewSchedule(prev => ({ ...prev, recipients: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-blue-500"
                  placeholder="john@company.com, jane@company.com"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Separate multiple email addresses with commas</p>
              </div>

              {/* Preview */}
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Schedule Preview</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {newSchedule.frequency === 'daily' && `Every day at ${newSchedule.send_time} IST`}
                  {newSchedule.frequency === 'weekly' && `Every ${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][newSchedule.send_day]} at ${newSchedule.send_time} IST`}
                  {newSchedule.frequency === 'monthly' && `On the ${newSchedule.send_day || 1}${['st', 'nd', 'rd'][((newSchedule.send_day || 1) - 1) % 10] || 'th'} of each month at ${newSchedule.send_time} IST`}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleReport}
                disabled={!newSchedule.name || !newSchedule.recipients}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Schedule Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportsPage;
